"use client";

import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FocusLock from "react-focus-lock";
import { v4 as uuid } from "uuid";

import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { ModalsContext } from "@/context/ModalsContext";
import { NavigationContext } from "@/context/NavigationContext";
import { TemplateContext } from "@/context/TemplateContext";
import { UserContext } from "@/context/UserContext";
import { browsePropertiesHeaderLinkClick, ctaClickTracking, navigationClickTracking, searchInteractionTracking } from "@/dataLayer/dataLayer";
import useScroll from "@/hooks/useScroll";
import { HeaderProps, PRODUCT_TYPE, VISUAL_VARIANT } from "@/types";

const SoftLogin = dynamic(() => import("@/components/SoftLogin/SoftLogin"), { ssr: false });
const ContactChannels = dynamic(() => import("@/components/ContactChannels/ContactChannels"), { ssr: false });

export const HEADER_DESKTOP = "header-desktop";
export const CONTECT_US = "Contact Us";

const whiteMenu = {
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
  textColor: "text-black",
  bgColor: "bg-white shadow-strong",
};

const whiteMenuNoShadow = {
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
  textColor: "text-black",
  bgColor: "bg-white border border-b-gray-10",
};

const transparentMenu = {
  darkLogo: "hidden",
  lightLogo: "block",
  iconStroke: "stroke-white",
  hamburgerVariant: "light",
  textColor: "text-black",
  bgColor: "bg-transparent shadow-none",
};

const currencyAndMeasurement = {
  currency: {
    AED: {
      en: "AED",
      ar: "درهم",
      ru: "дирхам",
    },
    USD: {
      en: "USD",
      ar: "دولار أمريكي",
      ru: "доллар США",
    },
    CNY: {
      en: "CNY",
      ar: "китайский юань",
      ru: "Yuan chinois",
    },
    RUB: {
      en: "RUB",
      ar: "روبل روسي",
      ru: "Rouble russe",
    },
    EUR: {
      en: "EUR",
      ar: "فرك",
      ru: "руб",
    },
    GBP: {
      en: "GBP",
      ar: "جنيه إسترليني",
      ru: "Фунт стерлингов",
    },
    INR: {
      en: "INR",
      ar: "روبية هندية",
      ru: "индийская рупия",
    },
  },
  measurement: {
    imperial: {
      en: "Imperial",
      ar: "النظام الإمبراطوري",
      ru: "имперский",
    },
    metric: {
      en: "Metric",
      ar: "النظام المتري",
      ru: "метрика",
    },
  },
};

const HeaderDesktop: FC<HeaderProps> = ({ logoDark, logoLight, menu, personalArea, contactChannels, onLogin, search, settings }) => {
  const scroll = useScroll();
  const t = useTranslations();
  const locale = useLocale();
  const desktopHeaderRef = useRef<HTMLDivElement>(null);
  const { fixedHeader } = useContext(TemplateContext);
  const { setContactUsModalOpen } = useContext(ModalsContext);
  const { language, currency, measurement, isLoggedIn } = useContext(UserContext);
  const router = useRouter();

  const pathname = usePathname();
  const {
    desktopPanelID,
    setDesktopPanelID,
    setSettingsVisible,
    setLanguagesVisible,
    languagesVisible,
    settingsVisible,
    transitionComplete,
    setDesktopHeaderHeight,
  } = useContext(NavigationContext);

  const scrollClasses: Record<string, string> = useMemo(() => {
    // quando è sticky dev'essre sempre white
    if (!fixedHeader) {
      return pathname?.includes("search") ? whiteMenuNoShadow : whiteMenu;
    } else {
      // in questo caso è fixed, trasparente con scroll a 0
      if (scroll === 0) {
        if (desktopPanelID !== "") {
          return pathname?.includes("search") ? whiteMenuNoShadow : whiteMenu;
        } else {
          return pathname?.includes("terms-and-conditions") || pathname?.includes("privacy-policy") ? whiteMenu : transparentMenu;
        }
      } else {
        return pathname?.includes("search") ? whiteMenuNoShadow : whiteMenu;
      }
    }
  }, [scroll, desktopPanelID, fixedHeader, pathname]);

  const keyboardHandler = useCallback(
    (event: KeyboardEvent) => {
      if (desktopPanelID !== "" && event.key.toLowerCase() === "escape") {
        setDesktopPanelID("");
      }
    },
    [desktopPanelID, setDesktopPanelID],
  );

  useEffect(() => {
    if (document) {
      document.addEventListener("keydown", keyboardHandler);
      return () => {
        document.removeEventListener("keydown", keyboardHandler);
      };
    }
  }, [keyboardHandler]);

  const languageCallback = useCallback(() => {
    if (settingsVisible) {
      setLanguagesVisible(languagesVisible);
    }
    if (desktopPanelID) {
      setDesktopPanelID("");
    }
    setLanguagesVisible(!languagesVisible);
  }, [settingsVisible, languagesVisible, desktopPanelID, setDesktopPanelID, setLanguagesVisible]);

  const settingsCallback = useCallback(() => {
    if (languagesVisible) {
      setLanguagesVisible(settingsVisible);
    }
    if (desktopPanelID) {
      setDesktopPanelID("");
    }
    setSettingsVisible(!settingsVisible);
  }, [settingsVisible, languagesVisible, desktopPanelID, setDesktopPanelID, setLanguagesVisible, setSettingsVisible]);

  useEffect(() => {
    if (desktopHeaderRef.current) {
      setDesktopHeaderHeight(desktopHeaderRef.current.clientHeight);
    }
  }, [desktopHeaderRef, setDesktopHeaderHeight]);

  const panelVariation = {
    open: { opacity: 1, display: "block" },
    close: { opacity: 0, transitionEnd: { display: "none" } },
  };
  const transition = {
    duration: 0.3,
  };

  const loginHandler = (success: boolean, otpError?: boolean) => onLogin && onLogin(success, otpError);

  return (
    <div ref={desktopHeaderRef} id={HEADER_DESKTOP}>
      {/* Main header */}
      <div className="backdrop-blur-2xl">
        <div className={`container flex  py-l px-xxl h-[60px]`}>
          <div className="flex justify-between items-center w-full">
            <AnchorLink className="block max-w-[133px] md:max-w-[114px] max-h-[30px]" href={`/${locale}/`} aria-label={t("button.label.homepage")}>
              {/* BUG 88996 h-full removed from className */}
              <Image
                fit="pad"
                loading="eager"
                className={`block w-auto ${scrollClasses.darkLogo}`}
                src={logoDark?.url}
                width={114}
                height={30}
                alt={logoDark?.title}
              />
              <Image
                fit="pad"
                loading="eager"
                className={`block w-auto ${scrollClasses.lightLogo}`}
                src={logoDark?.url}
                width={114}
                height={30}
                alt={logoDark?.title}
              />
            </AnchorLink>
            <nav className="flex gap-l">
              {menu?.items?.map((item, index) => {
                return (
                  <div key={`${item.id}-desktop-${index}`}>
                    <Button
                      className={`${desktopPanelID === item.id && "underline"} hover:underline underline-offset-4 font-secondary cursor-pointer
 text-sm flex justify-between items-center ${scrollClasses.textColor}`}
                      variant={ButtonVariant.TERTIARY}
                      size={ButtonSize.SMALL}
                      theme={ButtonTheme.BLACK}
                      type="button"
                      onClick={() => {
                        console.log("item", item);
                        if (CONTECT_US === item?.title) {
                          setContactUsModalOpen(true);
                        } else if (item?.urlSlug && !pathname.includes(item.urlSlug)) {
                          router.push(item?.urlSlug);
                        }
                      }}
                    >
                      <span>{item.title}</span>
                    </Button>
                  </div>
                );
              })}
            </nav>
          </div>
          <nav className="flex gap-l items-center">
            {/* TODO ti opens automatically */}
            {personalArea && personalArea?.link && (
              <AnchorLink className="hidden cursor-pointer" href={isLoggedIn ? personalArea?.link?.url : ""} span={!isLoggedIn}>
                <div className="flex flex-col items-center">
                  <SoftLogin
                    iconClass={scrollClasses.iconStroke}
                    icon={{ notLoggedIcon: "user-unselected", loggedIcon: "user-selected" }}
                    onLogin={loginHandler}
                  />
                </div>
              </AnchorLink>
            )}
          </nav>
        </div>
      </div>
      {/* Navigation panels */}
      {menu?.items?.map((item, index) => {
        const id = uuid();
        const active = desktopPanelID === item.id;
        return (
          <motion.div
            key={`subpanel-desktop-${item.id}-${index}-${id}`}
            id={`modal-panel-desktop-${item.id}-${index}`}
            initial="close"
            variants={panelVariation}
            animate={active ? "open" : "close"}
            transition={transition}
            className={`backdrop-blur-2xl pt-m absolute top-[100%] bg-transparent w-full h-[calc(100dvh-96px)] overflow-auto`}
          >
            <div
              onClick={() => {
                setDesktopPanelID("");
              }}
              className="absolute left-0 top-0 w-full h-full"
            ></div>
            <div className="container">
              <FocusLock
                /* returnFocus={(el) => {
                    if (!trigger) {
                      setTrigger(el as HTMLElement);
                    } else {
                      trigger.focus();
                      setTrigger(undefined);
                    }
                    return true;
                  }} */
                disabled={!active}
              >
                <div className="bg-white p-xl pb-m rounded-2xl relative">
                  <div className="flex gap-xs justify-between">
                    {item?.columns?.map((column, index) => {
                      return (
                        <div key={`desktop-${column?.id}`} className={cn(index === 0 ? "grow" : "flex-1")}>
                          <p className="py-s text-xs uppercase text-gray-50 font-primary font-medium">{column?.title}</p>
                          <ul>
                            {column?.links?.map((link) => {
                              return (
                                <li key={`desktop-menu-link-${link.id}`}>
                                  <Button
                                    className={`${menu?.highlightFirstCluster && index === 0 ? "text-l py-s" : "text-sm py-xs"}  w-full no-underline flex justify-between items-center font-secondary font-medium hover:underline`}
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    theme={ButtonTheme.BLACK}
                                    href={getLink(link, locale)}
                                    onClick={() => browsePropertiesHeaderLinkClick({ link_category: column?.title, link_name: link?.label })}
                                  >
                                    {link?.label}
                                  </Button>
                                </li>
                              );
                            })}
                            {column?.viewAll && (
                              <li>
                                <Button
                                  className={`${menu.highlightFirstCluster && index === 0 ? "text-l py-s" : "text-sm py-xs"}  w-full no-underline flex justify-between items-center font-secondary font-medium hover:underline`}
                                  variant={ButtonVariant.TERTIARY}
                                  size={ButtonSize.SMALL}
                                  theme={ButtonTheme.BLACK}
                                  href={getLink(column.viewAll, locale)}
                                  onClick={() =>
                                    browsePropertiesHeaderLinkClick({ link_category: column?.title, link_name: column?.viewAll?.label || "" })
                                  }
                                >
                                  {column?.viewAll?.label}
                                </Button>
                              </li>
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                  {item?.menuItemCta?.link && (
                    <>
                      <hr className="my-xl border-white border-b border-b-gray-10" />
                      <div className="text-center">
                        <Button
                          href={getLink(item?.menuItemCta, locale)}
                          customClass="block w-fit mx-auto"
                          variant={ButtonVariant.SECONDARY}
                          size={ButtonSize.MEDIUM}
                          theme={ButtonTheme.BLACK}
                          type="button"
                        >
                          {item.menuItemCta.label}
                        </Button>
                      </div>
                    </>
                  )}
                  <button
                    className="absolute right-m top-m"
                    onClick={() => {
                      setDesktopPanelID("");
                    }}
                  >
                    <Icon name="close" className="stroke-black" size="24" title={t("label.common.close")} />
                  </button>
                </div>
              </FocusLock>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};
export default HeaderDesktop;
